import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  clearUserToken,
  getStoredUserProfile,
  getStoredUserToken,
  isUnauthorizedError,
  registerSessionClearHandler,
  storeUserProfile,
  storeUserToken,
} from "@/lib/sessionAuth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const clearing = useRef(false);

  const clearSession = useCallback(() => {
    if (clearing.current) return;
    clearing.current = true;
    try {
      clearUserToken();
      setUser(null);
    } finally {
      clearing.current = false;
    }
  }, []);

  // Bootstrap a web session from a one-time handoff code passed by the mobile
  // app via the URL fragment (e.g. #hc=<code>). Fragments are not sent to the
  // server, so the token never appears in access logs; the code is single-use
  // and short-lived. On success we store the exchanged token and strip the
  // fragment from the URL so it can't be re-read or bookmarked.
  const consumeHandoff = async () => {
    const hash = window.location.hash || "";
    const match = hash.match(/[#&]hc=([^&]+)/);
    if (!match) return false;
    const code = decodeURIComponent(match[1]);
    const cleaned = hash.replace(/([#&])hc=[^&]+/, "$1").replace(/[#&]$/, "");
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search + (cleaned && cleaned !== "#" ? cleaned : "")
    );
    try {
      const { data } = await api.post("/auth/handoff/exchange", { code });
      storeUserToken(data.token);
      storeUserProfile(data.user);
      setUser(data.user);
      return true;
    } catch {
      return false;
    }
  };

  const load = async () => {
    if (await consumeHandoff()) {
      setReady(true);
      return;
    }
    const token = getStoredUserToken();
    if (!token) {
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      storeUserProfile(data);
      setUser(data);
    } catch (e) {
      // Only wipe the 30-day JWT on 401 — network/5xx must not force OTP again.
      if (isUnauthorizedError(e)) {
        clearSession();
      } else {
        const cached = getStoredUserProfile();
        if (cached) setUser(cached);
      }
    }
    setReady(true);
  };

  useEffect(() => {
    const unregister = registerSessionClearHandler(() => setUser(null));
    load();
    return unregister;
  }, [clearSession]);

  const requestOtp = async (phone) => {
    const { data } = await api.post("/auth/otp/request", { phone });
    return data;
  };

  const verifyOtp = async (phone, code, name, { acceptLegal = false } = {}) => {
    const { data } = await api.post("/auth/otp/verify", {
      phone,
      code,
      name,
      accept_legal: acceptLegal,
    });
    storeUserToken(data.token);
    storeUserProfile(data.user);
    setUser(data.user);
    return data.user;
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      storeUserProfile(data);
      setUser(data);
    } catch (e) {
      if (isUnauthorizedError(e)) clearSession();
    }
  };

  const logout = () => {
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ user, ready, requestOtp, verifyOtp, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
